/* eslint-disable @typescript-eslint/no-explicit-any */
/* Multi-select editing: with 2+ visuals selected, the Format pane edits ALL of them.
 *
 * The pane itself is built around ONE widget (the primary one). Instead of rewriting
 * every control, a pane edit is turned into a list of path changes (what really
 * differs between the primary before/after the edit) and replayed on the others:
 *
 *  - universal look keys (title/card/border/...)  -> every selected visual
 *  - group look keys (chart, numberFormat, ...)    -> visuals of the same kind
 *  - everything else under display                -> visuals of the same type
 *  - hidden / locked                               -> every selected visual
 *  - per-visual things (title text, data, layout, alt text) -> never fanned out
 *
 * Pure functions, no React: see web/scripts/multi-edit.check.ts. */
import { CHART_DATA_KEYS, GROUP_KEYS, UNIVERSAL_KEYS, sameKind } from "./formatPainter";
import type { SPage, SWidget } from "./model";

export interface PathChange {
  path: string[];
  /** undefined = remove the key */
  value: any;
}

const isObj = (v: unknown): v is Record<string, any> => v !== null && typeof v === "object" && !Array.isArray(v);

function equal(a: any, b: any): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Leaf-level differences between two plain objects (arrays are leaves). */
export function diffPaths(before: any, after: any, prefix: string[] = []): PathChange[] {
  // A key that appears/disappears as a whole object is expanded to its leaves, so per-key rules still apply.
  const b = isObj(before) ? before : before === undefined && isObj(after) ? {} : null;
  const a = isObj(after) ? after : after === undefined && isObj(before) ? {} : null;
  if (b && a) {
    const out: PathChange[] = [];
    for (const k of new Set([...Object.keys(b), ...Object.keys(a)])) out.push(...diffPaths(b[k], a[k], [...prefix, k]));
    return out;
  }
  return equal(before, after) ? [] : [{ path: prefix, value: after }];
}

function setPath(obj: any, path: string[], value: any): any {
  const [k, ...rest] = path;
  const base = isObj(obj) ? { ...obj } : {};
  if (!rest.length) {
    if (value === undefined) delete base[k];
    else base[k] = value;
  } else {
    const child = setPath(base[k], rest, value);
    if (child === undefined || (isObj(child) && !Object.keys(child).length)) delete base[k];
    else base[k] = child;
  }
  return base;
}

/** What a pane edit of `primary` changed. */
export function changesFromPatch(primary: SWidget, patch: Partial<SWidget>): PathChange[] {
  return diffPaths(primary, { ...primary, ...patch });
}

/** Should this change also be written to `target`? */
export function appliesTo(primary: SWidget, target: SWidget, change: PathChange): boolean {
  const [root, key, sub] = change.path;
  if (root === "hidden" || root === "locked") return true;
  if (root !== "display" || !key) return false;
  if ((UNIVERSAL_KEYS as readonly string[]).includes(key)) return true;
  if ((GROUP_KEYS as readonly string[]).includes(key)) {
    if (target.type === primary.type) return true;
    if (!sameKind(primary.type, target.type)) return false;
    // chart kind / per-series settings only travel between identical types
    return !(key === "chart" && sub && (CHART_DATA_KEYS as readonly string[]).includes(sub));
  }
  return target.type === primary.type;
}

export function applyChanges(widget: SWidget, changes: PathChange[]): SWidget {
  let next: any = widget;
  for (const c of changes) next = setPath(next, c.path, c.value);
  if (widget.display && !next.display) next = { ...next, display: {} }; // pruning never drops display itself
  return next;
}

/** Apply one pane edit (made on `primaryId`) to every selected visual of the page, as ONE page update.
 *  `changed` counts visuals other than the primary that actually changed. */
export function applyEditToSelection(page: SPage, ids: string[], primaryId: string, patch: Partial<SWidget>): { page: SPage; changed: number } {
  const primary = page.widgets.find((w) => w.id === primaryId);
  if (!primary) return { page, changed: 0 };
  const changes = changesFromPatch(primary, patch);
  const sel = new Set(ids);
  let changed = 0;
  const widgets = page.widgets.map((w) => {
    if (w.id === primaryId) return { ...w, ...patch };
    if (!sel.has(w.id)) return w;
    const mine = changes.filter((c) => appliesTo(primary, w, c));
    if (!mine.length) return w;
    const next = applyChanges(w, mine);
    if (!equal(next, w)) changed += 1;
    return next;
  });
  return { page: { ...page, widgets }, changed };
}

export function isMixedTypes(widgets: SWidget[]): boolean {
  return new Set(widgets.map((w) => w.type)).size > 1;
}

/** Display settings (top level + one level down) whose value is not the same on every visual. */
export function mixedSettings(widgets: SWidget[]): string[] {
  if (widgets.length < 2) return [];
  const flat = (w: SWidget): Map<string, string> => {
    const m = new Map<string, string>();
    const walk = (v: any, path: string[]) => {
      if (isObj(v) && path.length < 2) for (const [k, x] of Object.entries(v)) walk(x, [...path, k]);
      else m.set(path.join("."), JSON.stringify(v));
    };
    walk(w.display ?? {}, []);
    return m;
  };
  const maps = widgets.map(flat);
  const keys = new Set(maps.flatMap((m) => [...m.keys()]));
  const out: string[] = [];
  for (const k of keys) {
    if (!k) continue;
    const vals = new Set(maps.map((m) => m.get(k) ?? "undefined"));
    if (vals.size > 1) out.push(k);
  }
  return out.sort();
}

const LABELS: Record<string, string> = {
  showTitle: "Show the title",
  titleStyle: "Title style",
  titleTemplate: "Dynamic title",
  cardStyle: "Card",
  showBorder: "Border",
  showLastRefresh: "Last refreshed",
  alignment: "Alignment",
  numberFormat: "Number format",
  format: "Number format",
  chart: "Chart settings",
  tablePresentation: "Table style",
  kpi: "Number card",
};

/** Plain-language names for mixedSettings() output, de-duplicated ("titleStyle.fontSize" -> "Title style"). */
export function mixedLabels(paths: string[]): string[] {
  const out: string[] = [];
  for (const p of paths) {
    const root = p.split(".")[0];
    const label = LABELS[root] ?? root.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
    if (!out.includes(label)) out.push(label);
  }
  return out;
}
